import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { MessageSquare, Send, X, Sparkles, Loader2 } from "lucide-react";

/**
 * Insight Agent — RAG chat drawer that can be dropped on any report page.
 * Pass `campaignId` to scope to a single campaign, omit for cross-campaign chat.
 */
export default function InsightChat({ campaignId }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]); // [{role:'user'|'agent', text, matches?}]
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", text: input.trim() };
    setHistory((h) => [...h, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/insights/chat", {
        message: userMsg.text,
        campaign_id: campaignId || null,
        history: history.map((m) => ({ role: m.role, text: m.text })),
      });
      setHistory((h) => [...h, { role: "agent", text: data.reply, matches: data.matches || [] }]);
    } catch (err) {
      setHistory((h) => [...h, { role: "agent", text: `Sorry — ${err.response?.data?.detail || err.message}`, matches: [] }]);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-[100] bg-[var(--rl-brand)] hover:bg-[var(--rl-text)] text-white px-5 py-3 font-semibold shadow-lg flex items-center gap-2 transition-colors"
        data-testid="insight-chat-open-btn"
      >
        <Sparkles className="w-4 h-4" /> Ask Insight Agent
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 z-[100] w-full sm:w-[440px] h-[70vh] bg-white border border-[var(--rl-border)] shadow-2xl flex flex-col" data-testid="insight-chat-drawer">
      <div className="flex items-center justify-between px-5 py-3 bg-[var(--rl-text)] text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[var(--rl-accent)]" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold">Insight Agent</div>
        </div>
        <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="insight-chat-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="insight-chat-history">
        {history.length === 0 && (
          <div className="text-sm text-[var(--rl-text-secondary)] leading-relaxed">
            <div className="font-semibold text-[var(--rl-text)] mb-1">Hi — I've read every panelist response in {campaignId ? "this campaign" : "your library"}.</div>
            Try: <em>"What's the biggest objection?"</em> · <em>"Who mentioned price?"</em> · <em>"Summarize the top themes."</em>
          </div>
        )}
        {history.map((m, i) => (
          <div key={`turn-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={`inline-block max-w-[85%] px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--rl-text)] text-white" : "bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)]"}`} data-testid={`insight-chat-msg-${i}`}>
              {m.text}
              {m.matches?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[var(--rl-border)] space-y-1.5">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">Sources</div>
                  {m.matches.slice(0, 3).map((src, j) => {
                    const p = src.prosody;
                    const d = src.prosody_delta;
                    return (
                      <div key={`src-${i}-${j}`} className="text-[11px] italic text-[var(--rl-text-secondary)] leading-snug" data-testid={`insight-chat-src-${i}-${j}`}>
                        "{(src.text || "").slice(0, 140)}{(src.text || "").length > 140 ? "…" : ""}"
                        {p && (
                          <div className="not-italic mt-1 inline-flex flex-wrap items-center gap-1.5 text-[9px] uppercase tracking-[0.15em] font-bold">
                            <span className="bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)] px-1.5 py-0.5 text-[var(--rl-text)]" data-testid={`src-emo-label-${i}-${j}`}>{p.label}</span>
                            {["arousal", "valence", "confidence"].map((k) => {
                              if (typeof p[k] !== "number") return null;
                              const dv = d && typeof d[k] === "number" ? d[k] : null;
                              const sign = dv == null ? "" : (dv >= 0 ? `+${Math.round(dv)}` : `${Math.round(dv)}`);
                              const color = k === "arousal" ? "var(--rl-accent)" : k === "valence" ? "var(--rl-brand)" : "#00C853";
                              return (
                                <span key={k} className="inline-flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5" style={{ background: color }} />
                                  {k[0].toUpperCase()}{Math.round(p[k])}
                                  {sign && <span className={dv >= 0 ? "text-[var(--rl-accent)]" : "text-[var(--rl-brand)]"}>({sign})</span>}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]" data-testid="insight-chat-thinking">
            <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
          </div>
        )}
      </div>
      <form onSubmit={send} className="border-t border-[var(--rl-border)] p-3 flex gap-2" data-testid="insight-chat-form">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the data…"
          className="flex-1 bg-white border border-[var(--rl-border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--rl-brand)]"
          disabled={sending}
          data-testid="insight-chat-input"
        />
        <button type="submit" disabled={!input.trim() || sending} className="bg-[var(--rl-brand)] hover:bg-[var(--rl-text)] text-white px-3 py-2 disabled:opacity-40 transition-colors" data-testid="insight-chat-send-btn">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
